// frontend/src/components/enroll/VehicleSelectionStep.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal/Modal';
import styled from 'styled-components';
import Typography from '../Typography';
import Button from '../Button/Button';
import { useVehicles } from '../../context/VehicleContext';
import { useAuth } from '../../context/AuthContext';
import AddVehicleModal from './AddVehicleModal';
import InfoModal from './InfoModal';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';
import AddIcon from './../Icons/AddIcon';
import ArrowRightIcon from './../Icons/ArrowRightIcon';
import ChevronDownIcon from './../Icons/ChevronDownIcon';
import { getVehicleIcon, getDefaultIconProps } from '../../utils/vehicleIcons';
import VehicleListItem from '../VehicleListItem/VehicleListItem';

const VehicleSelectionStep = ({ onVehicleSelected, onCancel, eventRequiresVehicle, allowedVehicleTypes = [] }) => {
  const { vehicles, fetchVehicles, loading } = useVehicles();
  const { user } = useAuth();
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showInvalidVehicles, setShowInvalidVehicles] = useState(false);
  const { t } = useTranslation(['eventEnroll', 'vehicleTypes']);

  // Si el evento requiere vehículo, mostramos el InfoModal al iniciar
  useEffect(() => {
    if (eventRequiresVehicle) {
      setShowInfoModal(true);
    }
  }, [eventRequiresVehicle]);

  // Llamamos a fetchVehicles al montar (si user existe)
  useEffect(() => {
    if (user && fetchVehicles) {
      fetchVehicles(user.id);
    }
  }, [user, fetchVehicles]);

  // Función para obtener los nombres de los tipos de vehículos permitidos
  const getAllowedVehicleTypeNames = () => {
    if (!allowedVehicleTypes || allowedVehicleTypes.length === 0) {
      return '';
    }

    return allowedVehicleTypes
      .map(type => t(`vehicleTypes:allowedVehicleTypes.${type}`, type))
      .join(', ');
  };

  // Separar vehículos válidos e inválidos
  const validVehicles = vehicles.filter(vehicle => {
    if (!allowedVehicleTypes || allowedVehicleTypes.length === 0) {
      return true;
    }
    const vehicleType = vehicle.vehicleType || 'motorcycle';
    return allowedVehicleTypes.includes(vehicleType);
  });

  const invalidVehicles = vehicles.filter(vehicle => {
    if (!allowedVehicleTypes || allowedVehicleTypes.length === 0) {
      return false;
    }
    const vehicleType = vehicle.vehicleType || 'motorcycle';
    return !allowedVehicleTypes.includes(vehicleType);
  });

  // Filtrar vehículos por tipos permitidos (mantener para compatibilidad)
  const filteredVehicles = validVehicles;

  const handleCardClick = (vehicleId) => {
    // Solo permitir selección de vehículos válidos
    const vehicle = vehicles.find(v => v._id === vehicleId);
    if (vehicle) {
      const vehicleType = vehicle.vehicleType || 'motorcycle';
      if (!allowedVehicleTypes || allowedVehicleTypes.length === 0 || allowedVehicleTypes.includes(vehicleType)) {
        setSelectedVehicleId(vehicleId);
      }
    }
  };

  const handleInvalidVehicleClick = () => {
    // No hacer nada al hacer click en vehículos inválidos
  };

  const handleContinue = () => {
    const vehicle = filteredVehicles.find((v) => v._id === selectedVehicleId);
    if (vehicle) {
      // Validar que el vehículo seleccionado sea de un tipo permitido
      const vehicleType = vehicle.vehicleType || 'motorcycle';
      if (!allowedVehicleTypes || allowedVehicleTypes.length === 0 || allowedVehicleTypes.includes(vehicleType)) {
        onVehicleSelected(vehicle);
      }
    }
  };

  return (
    <>
      <Modal title={t('vehicleSelection.title')} onClose={onCancel} maxWidth="500px" isOpen={true}>
        {loading ? (
          <>
            <Container>
              <Typography $variant="body-1-regular">
                {t('vehicleSelection.loading')}
              </Typography>
            </Container>
          </>
        ) : filteredVehicles.length === 0 ? (
          <>
            <Container>
              <EmptyState>
                <EmptyStateHeader>
                  <Typography $variant="title-5-semibold">
                    {allowedVehicleTypes && allowedVehicleTypes.length > 0
                      ? t('vehicleSelection.noCompatibleVehicles.title')
                      : t('vehicleSelection.emptyGarage.title')
                    }
                  </Typography>

                  {allowedVehicleTypes && allowedVehicleTypes.length > 0 ? (
                    <>
                      <Typography $variant="body-1-medium" $align="center" color={theme.colors.defaultWeak}>
                        {t('vehicleSelection.noCompatibleVehicles.allowedTypesLabel')} <span style={{ color: theme.colors.brandMain }}>{getAllowedVehicleTypeNames()}</span>
                      </Typography>
                      <Typography $variant="body-1-medium" $align="center" color={theme.colors.defaultWeak}>
                        {t('vehicleSelection.noCompatibleVehicles.subtitle')}
                      </Typography>
                    </>
                  ) : (
                    <Typography $variant="body-1-medium" $align="center" color={theme.colors.defaultWeak}>
                      {t('vehicleSelection.emptyGarage.subtitle')}
                    </Typography>
                  )}
                </EmptyStateHeader>
                <Button
                  onClick={() => setShowAddModal(true)}
                  size="l"
                >
                  <AddIcon fill={theme.colors.inverseMain} />
                  {t('vehicleSelection.addVehicle')}
                </Button>
              </EmptyState>
            </Container>
            <VehicleActions>
              <Button onClick={onCancel} $variant="outline" size="l">
                {t('vehicleSelection.cancel')}
              </Button>
              <Button onClick={handleContinue} disabled={!selectedVehicleId} size="l">
                {t('vehicleSelection.continue')}
                <ArrowRightIcon fill={theme.colors.inverseMain} />
              </Button>
            </VehicleActions>
          </>
        ) : (
          <>
            <Container>
              <VehiclesList>
                <VehicleAdd
                  onClick={() => setShowAddModal(true)}>
                  <AddIcon fill={theme.colors.defaultWeak} />
                  <Typography $variant="title-5-medium" color={theme.colors.defaultWeak}>
                    {t('vehicleSelection.addVehicle')}
                  </Typography>
                </VehicleAdd>

                {/* Vehículos válidos */}
                {validVehicles.length > 0 && (
                  <VehicleSection>
                    <Typography as="h3" $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
                      {/* {t('vehicleSelection.validVehicles', 'Vehículos compatibles')} */}
                      {t('vehicleSelection.validVehicles')}
                    </Typography>
                    {validVehicles.map((vehicle) => {
                      const VehicleIconComponent = getVehicleIcon(vehicle.vehicleType);
                      const iconProps = {
                        ...getDefaultIconProps(theme.fill.brandMain),
                        width: 80,
                        height: 60
                      };

                      return (
                        <VehicleCard
                          key={vehicle._id}
                          selected={selectedVehicleId === vehicle._id}
                          onClick={() => handleCardClick(vehicle._id)}
                        >
                          {vehicle.image ? (
                            <VehicleImage src={vehicle.image} alt={`${vehicle.brand} ${vehicle.model}`} />
                          ) : (
                            <VehicleIconPlaceholder>
                              <VehicleIconComponent {...iconProps} />
                            </VehicleIconPlaceholder>
                          )}
                          <VehicleInfo>
                            {vehicle.nickname ? (
                              <>
                                <Typography $variant="body-1-medium" color={theme.colors.defaultMain}>
                                  {vehicle?.brand}
                                  <Typography as="span" color={theme.colors.defaultStrong} style={{ marginLeft: '4px' }}>
                                    {vehicle?.model}
                                  </Typography>
                                </Typography>
                                <Typography $variant="title-5-semibold" color={theme.colors.defaultMain}>{vehicle.nickname}</Typography>
                                <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>{vehicle.year}</Typography>
                              </>
                            ) : (
                              <>
                                <Typography $variant="body-1-medium" color={theme.colors.defaultMain}>
                                  {vehicle?.brand}
                                </Typography>
                                <Typography $variant="title-5-semibold" color={theme.colors.defaultMain}>{vehicle.model}</Typography>
                                <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>{vehicle.year}</Typography>
                              </>
                            )}
                          </VehicleInfo>
                        </VehicleCard>
                      );
                    })}
                  </VehicleSection>
                )}

                {/* Vehículos inválidos */}
                {invalidVehicles.length > 0 && (
                  <VehicleSection>
                    <InvalidVehiclesHeader onClick={() => setShowInvalidVehicles(!showInvalidVehicles)}>
                      <Typography as="h3" $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultWeak}>
                        {t('vehicleSelection.invalidVehicles')}
                      </Typography>
                      <ChevronIconWrapper $isOpen={showInvalidVehicles}>
                        <ChevronDownIcon fill={theme.colors.defaultWeak} width={20} height={20} />
                      </ChevronIconWrapper>
                    </InvalidVehiclesHeader>
                    {showInvalidVehicles && (
                      <VehicleList>
                        {invalidVehicles.map((vehicle) => {
                          const VehicleIconComponent = getVehicleIcon(vehicle.vehicleType);
                          const iconProps = {
                            ...getDefaultIconProps(theme.colors.defaultWeak),
                            width: 80,
                            height: 60
                          };

                          return (
                            <VehicleListItem
                              key={vehicle._id}
                              vehicle={vehicle}
                              selected={false}
                              disabled={true}
                              onClick={handleInvalidVehicleClick}
                            />
                          );
                        })}
                      </VehicleList>
                    )}
                  </VehicleSection>
                )}
              </VehiclesList>
            </Container>
            <VehicleActions>
              <Button onClick={onCancel} $variant="outline" size="l">
                {t('vehicleSelection.cancel')}
              </Button>
              <Button onClick={handleContinue} disabled={!selectedVehicleId} size="l">
                {t('vehicleSelection.continue')}
                <ArrowRightIcon fill={theme.colors.inverseMain} />
              </Button>
            </VehicleActions>
          </>
        )}

      </Modal>
      {showAddModal && (
        <AddVehicleModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onVehicleSaved={(newVehicle) => {
            setSelectedVehicleId(newVehicle._id);
            setShowAddModal(false);
          }}
        />
      )}

      {showInfoModal && (
        <InfoModal
          isOpen={showInfoModal}
          // onContinue={() => setShowInfoModal(false)}
          // onClose={onCancel}
          onContinue={() => setShowInfoModal(false)}
          onClose={() => setShowInfoModal(false)}
        />
      )}
    </>
  );
};

VehicleSelectionStep.propTypes = {
  onVehicleSelected: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  eventRequiresVehicle: PropTypes.bool,
  allowedVehicleTypes: PropTypes.arrayOf(PropTypes.string),
};

export default VehicleSelectionStep;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const EmptyState = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 2px dashed ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  display: flex;
  flex-direction: column;  
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.sm};
`;

const EmptyStateHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
  width: 100%;
`;

const VehiclesList = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const VehicleSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const VehicleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const VehicleCard = styled.ul`
  align-items: flex-start;
  border: 2px solid ${({ selected, theme, $disabled }) =>
    $disabled
      ? theme.border.defaultSubtle
      : (selected ? theme.colors.brandMain : theme.border.defaultSubtle)
  };
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.xs};
  transition: border 0.3s ease;
  width: 100%;
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};

  &:hover {
    border-color: ${({ theme, $disabled }) =>
    $disabled
      ? theme.border.defaultSubtle
      : theme.colors.brandMain
  };
  }
`;

const VehicleInfo = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const VehicleActions = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  display: flex;
  gap: 8px;
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const VehicleImage = styled.img`
  aspect-ratio: 4 / 3;
  border-radius: 8px;
  height: 80px;
  object-fit: cover;
  width: auto;
`;

const VehicleIconPlaceholder = styled.div`
  align-items: center;
  aspect-ratio: 4 / 3;
  background-color: ${({ theme }) => theme.fill.inverseWeak};
  border-radius: 8px;
  display: flex;
  height: 80px;
  justify-content: center;
  width: auto;
`;

const VehicleAdd = styled.li`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 2px dashed ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  display: flex;
  flex-direction: row;  
  gap: ${({ theme }) => theme.sizing.xs};
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.sm};
  transition: all 0.3s ease-in-out;
  width: 100%;

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultSubtle};
  }
`;

const InvalidVehiclesHeader = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  // margin-bottom: ${({ theme }) => theme.sizing.xs};
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const ChevronIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;